import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useDeliveryPlan from './useDeliveryPlan.js';
import useToast from './useToast.js';
import useAfterDeliverySms from './useAfterDeliverySms.js';
import StepContent from './StepContent.jsx';
import ProblemModal from './ProblemModal.jsx';
import OfflineIndicator from '../offline/OfflineIndicator.jsx';
import { addToQueue } from '../offline/syncQueue.js';
import { getCampaignId, getToken } from '../api/client.js';
import { WifiOffIcon, PinIcon } from '../common/icons.jsx';

const STEPS = ['arrive', 'scan', 'signature', 'photo', 'appointment', 'done'];
const STEP_LABELS = {
  arrive: 'Arrivée',
  scan: 'Scan',
  signature: 'Signature',
  photo: 'Photo',
  appointment: 'RDV',
  done: 'Terminé',
};

/**
 * Main delivery flow screen -- one stop at a time.
 * Steps: arrive -> scan -> signature -> photo -> appointment -> done
 */
export default function DeliveryFlow() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { plan, stops, currentStop, currentIndex, loading, error, nextStop, goToStop, allDelivered } = useDeliveryPlan(planId);
  const [step, setStep] = useState('arrive');
  const [userOverrodeStep, setUserOverrodeStep] = useState(false);

  // Auto-detect if current stop is already delivered or all done
  // But only if user hasn't manually picked a step
  useEffect(() => {
    if (userOverrodeStep) return;
    if (allDelivered) {
      setStep('done');
    } else if (currentStop?.status === 'delivered') {
      setStep('done');
    } else if (currentStop?.status === 'arrived' || currentStop?.status === 'in_progress') {
      setStep('scan');
    }
  }, [currentStop, allDelivered, userOverrodeStep]);

  // Reset override when changing stop + load existing data for delivered stops
  useEffect(() => {
    setUserOverrodeStep(false);
    setExistingData(null);
    if (currentStop?.status === 'delivered' && currentStop?.client_id && online) {
      fetch(`/api/c/${campaignId}/clients/${currentStop.client_id}/delivery-summary`, {
        headers: { Authorization: `Bearer ${getToken()}`, 'X-Campaign-Id': campaignId },
      })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data) setExistingData(data); })
        .catch(() => {});
    }
  }, [currentIndex, currentStop?.status]);

  /** Navigate to a specific step manually */
  const goToStep = (stepName) => {
    setUserOverrodeStep(true);
    setStep(stepName);
  };
  const [scanned, setScanned] = useState([]);
  const [showSignature, setShowSignature] = useState(false);
  const [arrivalTime, setArrivalTime] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);
  const campaignId = getCampaignId();
  const { toast, showToast } = useToast();
  const { sendAfterDeliverySms } = useAfterDeliverySms({ showToast });
  const gpsIntervalRef = useRef(null);
  const [eta, setEta] = useState(null);
  const [showProblem, setShowProblem] = useState(false);
  const [showStopList, setShowStopList] = useState(false);
  const [nearbyAlert, setNearbyAlert] = useState(false);
  const [existingData, setExistingData] = useState(null); // delivery summary for delivered stops
  const nearbyShownRef = useRef(null); // track which stop we alerted for

  useEffect(() => {
    const sendGps = () => {
      if (!navigator.geolocation || !online) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          // Geofencing: check proximity to current stop (< 150m)
          if (currentStop?.lat && currentStop?.lng && step === 'arrive') {
            const R = 6371e3;
            const dLat = (currentStop.lat - pos.coords.latitude) * Math.PI / 180;
            const dLng = (currentStop.lng - pos.coords.longitude) * Math.PI / 180;
            const a = Math.sin(dLat / 2) ** 2 + Math.cos(pos.coords.latitude * Math.PI / 180) * Math.cos(currentStop.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
            const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            if (dist < 150 && nearbyShownRef.current !== currentStop.id) {
              nearbyShownRef.current = currentStop.id;
              setNearbyAlert(true);
              if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 100]);
            }
          }

          // Calculate ETA to current stop
          if (currentStop?.lat && currentStop?.lng) {
            fetch(`https://router.project-osrm.org/route/v1/driving/${pos.coords.longitude},${pos.coords.latitude};${currentStop.lng},${currentStop.lat}?overview=false`)
              .then((r) => r.json())
              .then((data) => {
                if (data.routes?.[0]) {
                  const min = Math.round(data.routes[0].duration / 60);
                  const km = (data.routes[0].distance / 1000).toFixed(1);
                  setEta({ min, km });
                }
              })
              .catch(() => {});
          }
          fetch(`/api/c/${campaignId}/gps`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getToken()}`,
              'X-Campaign-Id': campaignId,
            },
            body: JSON.stringify({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              timestamp: new Date().toISOString(),
            }),
          }).catch(() => {});
        },
        () => {},
      );
    };
    sendGps();
    gpsIntervalRef.current = setInterval(sendGps, 30000);
    return () => clearInterval(gpsIntervalRef.current);
  }, [campaignId, online]);

  /** @param {string} url @param {object} body @param {string} [method='POST'] */
  const apiCall = useCallback(async (url, body, method = 'POST') => {
    if (!online) {
      await addToQueue({ type: 'delivery', url, method, body });
      return null;
    }
    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
        'X-Campaign-Id': campaignId,
      },
      body: JSON.stringify(body),
    });
    return res.ok ? res.json() : null;
  }, [online, campaignId]);

  const updateStop = useCallback((body) => {
    if (!currentStop) return Promise.resolve(null);
    return apiCall(`/api/c/${campaignId}/plans/${planId}/stops/${currentStop.id}`, body, 'PUT');
  }, [apiCall, campaignId, planId, currentStop]);

  const handleArrive = useCallback(async () => {
    const now = new Date().toISOString();
    setArrivalTime(now);
    const base = { status: 'arrived', actualArrival: now };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => updateStop({ ...base, gpsLat: pos.coords.latitude, gpsLng: pos.coords.longitude }),
        () => updateStop(base),
      );
    }
    setStep('scan');
  }, [updateStop]);

  const handleScanResult = useCallback(async (result) => {
    if (online) {
      await apiCall(`/api/c/${campaignId}/scan/validate`, { serialNumber: result.serialNumber });
    }
    setScanned((prev) => [...prev, result]);
  }, [apiCall, campaignId, online]);

  const handleSignature = useCallback(async (data) => {
    setShowSignature(false);
    if (currentStop) {
      await apiCall(`/api/c/${campaignId}/signatures`, {
        clientId: currentStop.client_id,
        planStopId: currentStop.id,
        signatureData: data.signatureData,
        signerName: data.signerName,
      });
    }
    setStep('photo');
  }, [apiCall, campaignId, currentStop]);

  const handlePhotoCaptured = useCallback(() => setStep('appointment'), []);
  const handlePhotoSkip = useCallback(() => setStep('appointment'), []);

  const handleAppointment = useCallback(async (data) => {
    if (currentStop) {
      await apiCall(`/api/c/${campaignId}/appointments`, {
        clientId: currentStop.client_id, planStopId: currentStop.id, ...data,
      });
    }
    if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
    setStep('done');
  }, [apiCall, campaignId, currentStop]);

  const handleProblem = useCallback(async (data) => {
    setShowProblem(false);
    if (currentStop) {
      await updateStop(data);
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      showToast('Problème signalé', 'error');
    }
    setStep('arrive');
    setScanned([]);
    setArrivalTime(null);
    nextStop();
  }, [updateStop, currentStop, nextStop, showToast]);

  const handleComplete = useCallback(async () => {
    if (currentStop) {
      await updateStop({ status: 'delivered', actualDeparture: new Date().toISOString() });
      if (online) await sendAfterDeliverySms(currentStop.id);
    }
    // Auto-navigate to next stop
    const nextIdx = currentIndex + 1;
    if (nextIdx < stops.length) {
      const next = stops[nextIdx];
      if (next?.lat && next?.lng) {
        // Detect iOS vs Android for optimal deep link
        const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const url = isIos
          ? `maps://maps.apple.com/?daddr=${next.lat},${next.lng}&dirflg=d`
          : `https://www.google.com/maps/dir/?api=1&destination=${next.lat},${next.lng}&travelmode=driving`;
        window.open(url, '_blank');
      }
    }
    setStep('arrive');
    setScanned([]);
    setArrivalTime(null);
    setNearbyAlert(false);
    nearbyShownRef.current = null;
    nextStop();
  }, [updateStop, currentStop, currentIndex, stops, nextStop, sendAfterDeliverySms, online]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-spin h-10 w-10 border-4 border-[#1e40af] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#f8fafc]">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center max-w-sm">
          <p className="text-red-800 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  const stepIndex = STEPS.indexOf(step);

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20">
      <OfflineIndicator />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'error' ? 'bg-[#dc2626] text-white' : 'bg-[#059669] text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-[#0f172a] to-[#1e40af] text-white px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 active:bg-white/30"
              aria-label="Retour"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <h1 className="font-bold text-lg">Arrêt {currentIndex + 1}/{stops.length}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowStopList(!showStopList)}
              className="text-xs bg-white/15 hover:bg-white/25 px-2.5 py-1.5 rounded-lg font-medium"
            >
              Ma tournée
            </button>
            <span className="text-sm opacity-80">{plan?.date ? new Date(plan.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' }) : ''}</span>
          </div>
        </div>
        {currentStop && (
          <div className="mt-2 flex items-start gap-2">
            <PinIcon size={16} className="mt-0.5 opacity-70 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm">{currentStop.client_name || 'Client'}</p>
              <p className="text-xs opacity-70">{currentStop.client_address || ''}</p>
              {eta && step === 'arrive' && (
                <p className="text-xs mt-1 bg-white/20 rounded px-2 py-0.5 inline-block">
                  🚴 {eta.km} km — ~{eta.min} min
                </p>
              )}
            </div>
          </div>
        )}

        {/* Stepper — clickable to review/edit steps */}
        <Stepper steps={STEPS} labels={STEP_LABELS} current={stepIndex}
          onStepClick={goToStep} allCompleted={currentStop?.status === 'delivered'} />
      </header>

      {/* Offline banner */}
      {!online && (
        <div className="mx-4 mt-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-800">
          <WifiOffIcon size={16} />
          <span className="font-medium">Mode hors ligne</span>
        </div>
      )}

      {/* Stop list drawer */}
      {showStopList && (
        <div className="mx-4 mt-3 bg-white rounded-xl shadow-sm border border-gray-200 p-3 space-y-1.5 max-h-64 overflow-y-auto">
          <div className="flex justify-between items-center mb-1">
            <p className="text-xs font-semibold text-[#0f172a] uppercase">Arrêts ({stops.length})</p>
            <button onClick={() => setShowStopList(false)} className="text-xs text-gray-400">Fermer</button>
          </div>
          {stops.map((s, i) => {
            const isDone = s.status === 'delivered' || s.status === 'skipped';
            const isCurrent = i === currentIndex;
            const isProblem = s.status === 'problem';
            return (
              <button key={s.id} onClick={() => { goToStop(i); setUserOverrodeStep(false); setStep(isDone ? 'done' : s.status === 'arrived' ? 'scan' : 'arrive'); setShowStopList(false); setScanned([]); }}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-left ${
                isCurrent ? 'bg-blue-50 border border-blue-200' : isDone ? 'bg-gray-50' : isProblem ? 'bg-red-50' : 'hover:bg-gray-50'
              }`}>
                <span className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  isDone ? 'bg-green-500 text-white' : isProblem ? 'bg-red-500 text-white' : isCurrent ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {isDone ? '\u2713' : isProblem ? '!' : i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-medium truncate ${isDone ? 'text-gray-400 line-through' : 'text-[#0f172a]'}`}>{s.client_name || 'Client'}</p>
                  <p className="text-[10px] text-gray-400">{s.units_count || 0} unités</p>
                </div>
                {isCurrent && !isDone && <span className="text-[10px] text-blue-600 font-semibold flex-shrink-0">EN COURS</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Geofencing proximity alert */}
      {nearbyAlert && step === 'arrive' && (
        <div className="mx-4 mt-3 bg-green-50 border border-green-300 rounded-xl px-4 py-3 flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-2">
            <span className="text-lg">📍</span>
            <span className="text-sm font-medium text-green-800">Vous etes a proximite du client</span>
          </div>
          <button
            onClick={() => { setNearbyAlert(false); handleArrive(); }}
            className="bg-green-600 text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            Confirmer
          </button>
        </div>
      )}

      <main className="p-4 space-y-4">
        <StepContent
          step={step} currentStop={currentStop} scanned={scanned}
          online={online} arrivalTime={arrivalTime} showSignature={showSignature}
          isLastStop={currentIndex >= stops.length - 1}
          isDelivered={currentStop?.status === 'delivered'}
          existingData={existingData}
          onProblem={() => setShowProblem(true)}
          onArrive={handleArrive} onScanResult={handleScanResult}
          onFinishScan={() => setStep('signature')}
          onShowSignature={() => setShowSignature(true)}
          onSignature={handleSignature}
          onCancelSignature={() => setShowSignature(false)}
          onPhotoCaptured={handlePhotoCaptured} onPhotoSkip={handlePhotoSkip}
          onAppointment={handleAppointment}
          onSkipAppointment={() => setStep('done')}
          onComplete={handleComplete} plan={plan}
        />
        {showProblem && currentStop && (
          <ProblemModal
            stopId={currentStop.id}
            planId={planId}
            onSubmit={handleProblem}
            onCancel={() => setShowProblem(false)}
          />
        )}
      </main>
    </div>
  );
}

/**
 * Horizontal stepper showing delivery progress.
 * Steps are clickable to navigate back to review/edit.
 * When allCompleted=true (stop delivered), all steps stay green with checkmarks.
 * @param {{ steps: string[], labels: Record<string,string>, current: number, onStepClick?: (step: string) => void, allCompleted?: boolean }} props
 */
function Stepper({ steps, labels, current, onStepClick, allCompleted }) {
  return (
    <div className="flex items-center mt-4 gap-0">
      {steps.map((s, i) => {
        const done = allCompleted ? (i !== current) : (i < current);
        const active = i === current;
        return (
          <div key={s} className="flex items-center flex-1">
            <button
              type="button"
              onClick={() => onStepClick?.(s)}
              className="flex flex-col items-center flex-1 cursor-pointer"
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                active ? 'bg-white text-[#1e40af] ring-2 ring-white shadow-lg scale-110'
                  : done || allCompleted ? 'bg-[#059669] text-white'
                    : 'bg-white/20 text-white/50'
              }`}>
                {active ? i + 1 : (done || allCompleted) ? '\u2713' : i + 1}
              </div>
              <span className={`text-[9px] mt-1 ${
                active || done || allCompleted ? 'text-white/90 font-medium' : 'text-white/40'
              }`}>
                {labels[s]}
              </span>
            </button>
            {i < steps.length - 1 && (
              <div className={`h-0.5 flex-1 mx-0.5 transition-colors ${
                (done || allCompleted) && !active ? 'bg-[#059669]' : 'bg-white/20'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}
